import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  ShieldAlert,
  Check,
  AlertTriangle,
  Copy,
  ExternalLink,
  Save,
  Trash2,
  Lock,
  Facebook,
  Instagram,
  Linkedin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Admin / Developer Setup panel.
 *
 * Lets allow-listed admins paste API credentials directly from the UI.
 * Values are saved to MongoDB and override .env defaults immediately —
 * no server restart required.
 *
 * Customers never see this component (admin-gated upstream).
 */

function XGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.97 6.817H1.673l7.73-8.835L1.247 2.25h6.835l4.713 6.231 5.449-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
    </svg>
  );
}

const PLATFORM_ICONS = {
  Facebook: { Comp: Facebook, color: "#1877F2" },
  Instagram: { Comp: Instagram, color: "#E4405F" },
  X: { Comp: XGlyph, color: "var(--text-1)" },
  TikTok: { Comp: null, color: "#000000" },
  LinkedIn: { Comp: Linkedin, color: "#0A66C2" },
};

function PlatformGlyph({ platform }) {
  const cfg = PLATFORM_ICONS[platform] || {};
  if (platform === "TikTok") {
    return (
      <div className="h-8 w-8 rounded-md flex items-center justify-center bg-[color:var(--surface-3)] text-[color:var(--text-1)] font-display font-bold text-xs">
        TT
      </div>
    );
  }
  const Comp = cfg.Comp;
  return (
    <div
      className="h-8 w-8 rounded-md flex items-center justify-center"
      style={{ background: `${cfg.color}25`, color: cfg.color }}
    >
      {Comp ? <Comp className="h-4 w-4" /> : null}
    </div>
  );
}

function ConfiguredBadge({ configured }) {
  return (
    <div
      className={
        "text-[11px] inline-flex items-center gap-1 rounded-full px-2 py-0.5 " +
        (configured
          ? "bg-[color:rgba(46,204,113,0.12)] text-[color:var(--success)]"
          : "bg-[color:rgba(255,138,61,0.12)] text-[color:var(--accent-warm)]")
      }
    >
      {configured ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {configured ? "Configured" : "Not configured"}
    </div>
  );
}

function XTestPublishRow({ disabled }) {
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post("/admin/connections/x/test-publish", { text: text.trim() });
      setResult(res.data);
      if (res.data.ok) {
        toast.success("Tweet published successfully");
      } else {
        toast.error(res.data.error || `X API returned ${res.data.status_code}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Test publish failed";
      setResult({ ok: false, error: detail, status_code: e?.response?.status });
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3" data-testid="admin-x-test-publish">
      <div className="font-display text-sm font-semibold text-[color:var(--text-1)] mb-1">
        Test publish to X
      </div>
      <div className="text-xs text-[color:var(--text-3)] mb-2 leading-relaxed">
        Sends a text-only tweet using your own X connection (sign in as the admin and connect X first).
        Useful for verifying credentials and scopes end-to-end. Returns the raw X API response.
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={`Leave blank for an auto-generated timestamped test tweet.`}
        rows={2}
        maxLength={280}
        disabled={busy || disabled}
        className="w-full rounded-md bg-[color:var(--bg-0)] border border-[color:var(--border-subtle)] text-[color:var(--text-1)] text-xs px-2 py-1.5 font-mono resize-none"
        data-testid="admin-x-test-publish-input"
      />
      <div className="flex items-center justify-between gap-2 mt-2">
        <div className="text-[10px] text-[color:var(--text-3)]">{text.length}/280</div>
        <Button
          size="sm"
          onClick={run}
          disabled={busy || disabled}
          className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
          data-testid="admin-x-test-publish-button"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />}
          Send test tweet
        </Button>
      </div>
      {result ? (
        <div className="mt-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--bg-0)] p-2 text-[11px] font-mono space-y-1" data-testid="admin-x-test-publish-result">
          <div className={result.ok ? "text-[color:var(--success)]" : "text-[color:var(--danger)]"}>
            {result.ok ? "✓ Success" : "✗ Failed"} · HTTP {result.status_code ?? "—"}
          </div>
          {result.tweet_id ? <div>tweet_id: <span className="text-[color:var(--text-1)]">{result.tweet_id}</span></div> : null}
          {result.tweet_url ? (
            <div>
              tweet_url:{" "}
              <a href={result.tweet_url} target="_blank" rel="noreferrer" className="text-[color:var(--accent-warm)] hover:underline break-all">
                {result.tweet_url}
              </a>
            </div>
          ) : null}
          {result.x_username ? <div>account: <span className="text-[color:var(--text-1)]">@{result.x_username}</span></div> : null}
          {result.scopes_available ? <div>scopes: <span className="text-[color:var(--text-2)]">{(result.scopes_available || []).join(" ")}</span></div> : null}
          {result.error ? <div className="text-[color:var(--danger)] break-all">error: {result.error}</div> : null}
          <details className="mt-1">
            <summary className="cursor-pointer text-[color:var(--text-3)]">Raw X response</summary>
            <pre className="mt-1 max-h-40 overflow-auto text-[10px] text-[color:var(--text-2)] whitespace-pre-wrap break-all">
              {JSON.stringify(result.raw_response ?? result, null, 2)}
            </pre>
          </details>
        </div>
      ) : null}
    </div>
  );
}

function FeatureFlagRow({ flag, disabled, onToggle }) {
  if (!flag) return null;
  const enabled = !!flag.enabled;
  return (
    <div
      className={
        "rounded-md border p-3 " +
        (enabled
          ? "border-[color:rgba(46,204,113,0.35)] bg-[color:rgba(46,204,113,0.06)]"
          : "border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.04)]")
      }
      data-testid={`admin-flag-${flag.key.toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-sm font-semibold text-[color:var(--text-1)]">
              {flag.label}
            </div>
            <span
              className={
                "text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 " +
                (enabled
                  ? "bg-[color:rgba(46,204,113,0.18)] text-[color:var(--success)]"
                  : "bg-[color:rgba(255,138,61,0.18)] text-[color:var(--accent-warm)]")
              }
            >
              {enabled ? "Enabled" : "Locked"}
            </span>
          </div>
          {flag.description ? (
            <div className="text-xs text-[color:var(--text-2)] mt-1 leading-relaxed">{flag.description}</div>
          ) : null}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={disabled}
          onClick={() => onToggle(!enabled)}
          data-testid={`admin-flag-toggle-${flag.key.toLowerCase()}`}
          className={
            "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors " +
            (enabled ? "bg-[color:var(--brand-primary)]" : "bg-[color:var(--surface-3)]") +
            (disabled ? " opacity-50 cursor-not-allowed" : "")
          }
        >
          <span
            className={
              "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition " +
              (enabled ? "translate-x-5" : "translate-x-0")
            }
          />
        </button>
      </div>

      {flag.requirements?.length ? (
        <details className="mt-3 text-[11px] text-[color:var(--text-2)]">
          <summary className="cursor-pointer text-[color:var(--text-3)]">
            What's required before enabling this
          </summary>
          <ul className="mt-2 space-y-1 list-disc list-inside leading-relaxed">
            {flag.requirements.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function FieldRow({ field, draft, onChange, disabled, onCopy }) {
  if (!field) return null;
  const isSecret = field.is_secret;
  // Non-secret fields: prefill with saved value so admins see what's persisted.
  // Secret fields: never echo the saved value; rely on placeholder + "Saved" badge.
  const savedDisplayValue = !isSecret ? (field.value || "") : "";
  const inputValue = draft !== undefined ? draft : savedDisplayValue;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label
          htmlFor={`field-${field.key}`}
          className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]"
        >
          {field.label}
        </label>
        <div className="flex items-center gap-1.5">
          {field.present ? (
            <span className="text-[10px] inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-[color:rgba(46,204,113,0.12)] text-[color:var(--success)]">
              <Check className="h-2.5 w-2.5" /> Saved
              {field.source === "env" ? <span className="opacity-60 ml-0.5">(env)</span> : null}
            </span>
          ) : (
            <span className="text-[10px] inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-[color:rgba(255,138,61,0.12)] text-[color:var(--accent-warm)]">
              <AlertTriangle className="h-2.5 w-2.5" /> Missing
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Input
          id={`field-${field.key}`}
          type={isSecret ? "password" : "text"}
          autoComplete="off"
          spellCheck="false"
          placeholder={
            isSecret && field.present
              ? `Saved · ${field.masked_value || ""} (enter a new value to replace)`
              : field.placeholder || (isSecret ? "Paste secret value" : "Paste value")
          }
          value={inputValue}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="bg-[color:var(--bg-0)] border-[color:var(--border-subtle)] text-[color:var(--text-1)] font-mono text-xs"
          data-testid={`admin-cred-input-${field.key}`}
        />
        {!isSecret && (inputValue || field.value) ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onCopy(inputValue || field.value)}
            disabled={disabled}
            className="h-9 px-2"
            title="Copy current value"
            data-testid={`admin-cred-copy-${field.key}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
      {/* Redirect URI: show suggested below the field */}
      {!isSecret && field.suggested_value ? (
        <div className="text-[11px] text-[color:var(--text-3)] flex items-center gap-1.5 flex-wrap">
          <span>Suggested:</span>
          <code className="rounded bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] px-1.5 py-0.5 text-[color:var(--text-1)] break-all">
            {field.suggested_value}
          </code>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onChange(field.suggested_value)}
            disabled={disabled}
            className="h-6 px-2 text-[10px]"
            data-testid={`admin-cred-use-suggested-${field.key}`}
          >
            Use this
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onCopy(field.suggested_value)}
            disabled={disabled}
            className="h-6 px-2 text-[10px]"
            data-testid={`admin-cred-copy-suggested-${field.key}`}
          >
            <Copy className="h-3 w-3 mr-1" /> Copy
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function PlatformForm({ platform, onSaved }) {
  // drafts[key] = string when the admin has typed in that field;
  // undefined means "leave saved value untouched, show it in the input".
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  // When the platform data refreshes (e.g. after Save), discard all in-flight
  // drafts so the inputs re-render with the freshly persisted values.
  useEffect(() => {
    setDrafts({});
  }, [
    platform.id_field?.value,
    platform.id_field?.masked_value,
    platform.id_field?.present,
    platform.secret_field?.masked_value,
    platform.secret_field?.present,
    platform.redirect_field?.value,
    platform.redirect_field?.present,
  ]);

  const setDraft = (key, val) => setDrafts((d) => ({ ...d, [key]: val }));

  // A field is "dirty" only when the admin typed a value that differs from
  // what's currently saved. This prevents accidental clears when an admin
  // hits Save without changing anything.
  const dirtyFields = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(drafts)) {
      if (v === undefined) continue;
      const f = [platform.id_field, platform.secret_field, platform.redirect_field].find(
        (x) => x && x.key === k
      );
      if (!f) continue;
      const saved = f.is_secret ? null : (f.value || "");
      if (f.is_secret) {
        // Secrets are dirty whenever a non-empty value is typed,
        // OR an empty value is typed AND a secret is currently saved (= explicit clear).
        if (v !== "" || f.present) out[k] = v;
      } else if (v !== saved) {
        out[k] = v;
      }
    }
    return out;
  }, [drafts, platform.id_field, platform.secret_field, platform.redirect_field]);

  const hasChanges = Object.keys(dirtyFields).length > 0;

  const copy = (text) => {
    navigator.clipboard?.writeText(text || "");
    toast.success("Copied");
  };

  const save = async () => {
    if (!hasChanges) return;
    setBusy(true);
    try {
      await api.put(`/admin/credentials/${platform.platform}`, { fields: dirtyFields });
      toast.success(`${platform.label} saved`);
      // drafts will be reset by the useEffect once the parent refreshes.
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save credentials");
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Clear saved credentials for ${platform.label}? Customers won't be able to connect until you re-enter them.`)) return;
    setBusy(true);
    try {
      const fields = {};
      [platform.id_field, platform.secret_field, platform.redirect_field].forEach((f) => {
        if (f && f.key) fields[f.key] = "";
      });
      await api.put(`/admin/credentials/${platform.platform}`, { fields });
      toast.success(`${platform.label} credentials cleared`);
      onSaved?.();
    } catch (_e) {
      toast.error("Could not clear");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5"
      data-testid={`admin-cred-card-${platform.platform.toLowerCase()}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <PlatformGlyph platform={platform.platform} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-base font-semibold text-[color:var(--text-1)]">
              {platform.label}
            </div>
            {platform.coming_soon ? (
              <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:var(--surface-3)] text-[color:var(--text-2)] border border-[color:var(--border-subtle)]">
                {platform.platform === "LinkedIn" ? "Planned" : "Coming soon"}
              </span>
            ) : null}
            <ConfiguredBadge configured={platform.configured} />
          </div>
          {platform.extra_info ? (
            <div className="text-xs text-[color:var(--text-3)] mt-1">{platform.extra_info}</div>
          ) : null}
        </div>
      </div>

      {platform.instructions?.length ? (
        <details className="mb-4 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3">
          <summary className="cursor-pointer text-xs text-[color:var(--text-2)] flex items-center gap-2">
            <ExternalLink className="h-3 w-3" />
            Setup instructions
            {platform.docs_url ? (
              <a
                href={platform.docs_url}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ml-auto text-[color:var(--accent-warm)] hover:underline text-[11px]"
              >
                Open provider →
              </a>
            ) : null}
          </summary>
          <ol className="mt-2 space-y-1.5 text-[12px] text-[color:var(--text-2)] list-decimal list-inside leading-relaxed">
            {platform.instructions.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </details>
      ) : null}

      <div className="space-y-4">
        <FieldRow
          field={platform.id_field}
          draft={drafts[platform.id_field?.key]}
          onChange={(v) => setDraft(platform.id_field.key, v)}
          disabled={busy}
          onCopy={copy}
        />
        <FieldRow
          field={platform.secret_field}
          draft={drafts[platform.secret_field?.key]}
          onChange={(v) => setDraft(platform.secret_field.key, v)}
          disabled={busy}
          onCopy={copy}
        />
        {platform.redirect_field ? (
          <FieldRow
            field={platform.redirect_field}
            draft={drafts[platform.redirect_field?.key]}
            onChange={(v) => setDraft(platform.redirect_field.key, v)}
            disabled={busy}
            onCopy={copy}
          />
        ) : null}
        {(platform.feature_flags || []).map((flag) => (
          <FeatureFlagRow
            key={flag.key}
            flag={flag}
            disabled={busy}
            onToggle={async (next) => {
              setBusy(true);
              try {
                await api.put(`/admin/credentials/${platform.platform}`, {
                  fields: { [flag.key]: next ? "true" : "" },
                });
                toast.success(next ? `${flag.label}: enabled` : `${flag.label}: disabled`);
                onSaved?.();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not update flag");
              } finally {
                setBusy(false);
              }
            }}
          />
        ))}
        {platform.platform === "X" ? <XTestPublishRow disabled={busy} /> : null}
      </div>

      <div className="mt-5 pt-4 border-t border-[color:var(--border-subtle)] flex items-center justify-between gap-2 flex-wrap">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={clearAll}
          disabled={busy || (!platform.id_field?.present && !platform.secret_field?.present && !platform.redirect_field?.present)}
          className="text-[color:var(--danger)] hover:text-[color:var(--danger)] text-xs"
          data-testid={`admin-cred-clear-${platform.platform.toLowerCase()}`}
        >
          <Trash2 className="h-3 w-3 mr-1" /> Clear saved
        </Button>
        <div className="flex items-center gap-3">
          {hasChanges ? (
            <span className="text-[11px] text-[color:var(--accent-warm)]">
              {Object.keys(dirtyFields).length} unsaved change{Object.keys(dirtyFields).length === 1 ? "" : "s"}
            </span>
          ) : platform.configured ? (
            <span className="text-[11px] text-[color:var(--text-3)]">All saved</span>
          ) : null}
          <Button
            type="button"
            onClick={save}
            disabled={busy || !hasChanges}
            className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
            data-testid={`admin-cred-save-${platform.platform.toLowerCase()}`}
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save credentials
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminConnectionsPanel({ onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/credentials");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Admin unlock required");
      } else if (e?.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Could not load admin setup");
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-[color:var(--text-3)]" data-testid="admin-panel-loading">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading developer setup…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-4 text-xs text-[color:var(--text-2)]">
        You don't have access to the admin setup panel.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-connections-panel">
      <div className="rounded-md border border-[color:rgba(255,138,61,0.35)] bg-[color:rgba(255,138,61,0.06)] p-4 text-xs text-[color:var(--accent-warm)] flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
        <div className="leading-relaxed">
          <strong className="text-[color:var(--text-1)]">Admin / Developer setup.</strong>{" "}
          Paste your API credentials here. Changes save instantly — no server restart needed.
          Customers will see the Connect buttons activate as soon as a platform is fully configured.
          {data.fallback_env_path ? (
            <>
              {" "}You can also pre-load defaults in{" "}
              <code className="rounded bg-[color:var(--bg-0)] px-1 py-0.5 text-[11px]">{data.fallback_env_path}</code>.
            </>
          ) : null}
        </div>
      </div>

      {data.instagram_note ? (
        <div className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] px-4 py-3 text-xs text-[color:var(--text-2)] leading-relaxed">
          <Lock className="h-3.5 w-3.5 inline mr-1 text-[color:var(--text-3)]" />
          {data.instagram_note}
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {(data.platforms || []).map((p) => (
          <PlatformForm
            key={p.platform}
            platform={p}
            onSaved={() => {
              load();
              onChanged?.();
            }}
          />
        ))}
      </div>
    </div>
  );
}
